import React, { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { AuthUtils } from 'src/services/Auth/Auth.utils';
import { appRoutes, RouteObject } from './routes.types';
import { useAuthStore } from 'src/services/Auth/Auth.store';
import { usePreLoginAuthLocation } from 'src/routes/use-pre-login-auth-location';
import { useSaveHubspotUtmLocationEffect } from 'src/lib/services/hubspot/use-hubspot-saved-utm-location';
import { useAuth } from 'src/services/Auth';
import { useProfile } from 'src/models';
import { useBrandsQuery } from 'src/api/queries';

type ProtectedRouteProps = {
  route: RouteObject;
};

// TODO: This component should only be rendered once per route.
export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ route }) => {
  const location = useLocation();
  const { isLoggedIn } = useAuthStore();
  const { rememberLocation } = usePreLoginAuthLocation();
  const { refresh } = useAuth();
  const profile = useProfile({ enabled: false });
  const brandsQuery = useBrandsQuery();
  const hasBrands = !!brandsQuery?.data?.items?.length;

  useSaveHubspotUtmLocationEffect();

  useEffect(() => {
    // Invoke refresh token every hour
    const tokenRefreshInvertal = setInterval(() => refresh(), 60 * 60 * 1000);

    return () => {
      clearInterval(tokenRefreshInvertal);
    };
  }, []);

  if (route.underMaintenance) {
    return <Navigate to={appRoutes.maintenance} />;
  }

  // will save location to locale storage in case if login occurs during external login
  if (!isLoggedIn && route.restricted?.includes('guest')) {
    // not logged users in add-subsription page reroute specifically to register page
    if (location.pathname === appRoutes.addSubscriptionSubscribe) {
      return (
        <Navigate to={appRoutes.register} state={{ from: rememberLocation(location) }} replace />
      );
    }

    return <Navigate to={appRoutes.login} state={{ from: rememberLocation(location) }} replace />;
  }

  // ALL NEW USERS REDIRECTED TO ADD SUB CANT ESCAPE
  if (
    profile.isLoaded &&
    !profile.hasActiveSubscriptions &&
    location.pathname !== appRoutes.addSubscriptionPlanSelect &&
    location.pathname !== appRoutes.addSubscriptionSubscribe &&
    location.pathname !== appRoutes.stripePaymentLinkRedirect
  ) {
    return <Navigate to={appRoutes.addSubscriptionPlanSelect} state={{ from: location }} replace />;
  }

  if (
    profile.isLoaded &&
    profile.hasActiveSubscriptions &&
    !hasBrands &&
    !brandsQuery.isLoading &&
    location.pathname !== appRoutes.createBrand
  ) {
    return <Navigate to={appRoutes.createBrand} state={{ from: location }} replace />;
  }

  if (isLoggedIn) {
    const userType = AuthUtils.getPayload()?.user_type;

    // When visiting restricted areas redirect logged in users to dashboard
    if (route.restricted?.includes(userType as any) && hasBrands) {
      return <Navigate to={appRoutes.dashboard} replace />;
    }

    // has failed subscription payment
    if (
      !profile.eligibleForTrial &&
      !profile.hasActiveSubscriptions &&
      profile.hasFailedSubscriptions &&
      location.pathname !== appRoutes.accountOnHold &&
      location.pathname !== appRoutes.addSubscriptionPlanSelect &&
      location.pathname !== appRoutes.addSubscriptionSubscribe &&
      !location.pathname.includes('/subscription-cancelation/subscription')
    ) {
      return <Navigate to={appRoutes.accountOnHold} replace />;
    }

    // If user in accountOnHold page but has no failed subscriptions, redirect to dashboard
    if (
      location.pathname === appRoutes.accountOnHold &&
      !profile.hasFailedSubscriptions &&
      hasBrands
    ) {
      return <Navigate to={appRoutes.dashboard} replace />;
    }
  }

  return (
    <route.layout>
      <Outlet />
    </route.layout>
  );
};
